import { useState } from "react";
import { useForm } from "react-hook-form";
import { ClassNamesConfig, GroupBase } from "react-select";
import AsyncSelect from "react-select/async";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Form } from "@/components/ui/form";
import { Label } from "@/components/ui/label";
import {
  SearchIndicator,
  createClassNames,
} from "@/components/ui/react-select";
import { getSessionRoomSystems } from "@/features/room-systems/api/get-room-systems";
import { useOrganisation } from "@/features/select-organisation/api/get-my-organisation";
import { RoomSystemSelectOption } from "@/features/session/components/room-system-select-option";
import { RoomSystemOption } from "@/features/session/types/room-system-option";
import { queryClient } from "@/lib/react-query";
import { cn } from "@/lib/utils";
import { PublicEvent } from "@classview/shared-types";
import { zodResolver } from "@hookform/resolvers/zod";

import { useAddParticipantToEvent } from "../api/add-participant-to-event";
import { getEventQueryKey } from "../api/get-event";
import {
  type RegisterRoomSystemToEventSchema,
  registerRoomSystemToEventSchema,
} from "../types/register-room-system-to-event.schema";

import { RegisterRoomSystemToEventModalTable } from "./register-room-system-to-event-modal-table";

type RegisterRoomSystemToEventModalProps = {
  event: PublicEvent;
};

export function RegisterRoomSystemToEventModal({
  event,
}: RegisterRoomSystemToEventModalProps) {
  const { organisation } = useOrganisation();
  const [isOpen, setIsOpen] = useState(false);
  const [value, setValue] = useState("");
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const form = useForm<RegisterRoomSystemToEventSchema>({
    resolver: zodResolver(registerRoomSystemToEventSchema),
  });

  const [newRoomSystems, setNewRoomSystems] = useState<RoomSystemOption[]>([]);
  const {
    mutateAsync: addRoomSystemsToEvent,
    isPending,
    error,
    reset,
  } = useAddParticipantToEvent({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getEventQueryKey(event.id) });
      setNewRoomSystems([]);
      setIsOpen(false);
    },
  });

  const addRoomSystem = (roomSystem: RoomSystemOption) => {
    setNewRoomSystems([...newRoomSystems, roomSystem]);
  };

  const reactSelectClassNames = createClassNames({
    control: () => {
      return cn(!!error && "border-destructive");
    },
  });

  const loadOptions = async (input: string): Promise<RoomSystemOption[]> => {
    const roomSystems = await getSessionRoomSystems({
      organisationId: organisation.id,
      search: input,
    });
    const filteredRoomSystems =
      roomSystems?.items.filter(
        (roomSystem) =>
          !newRoomSystems.some(
            (newRoomSystem) => newRoomSystem.id === roomSystem.id,
          ) &&
          roomSystem.name.toLowerCase().includes(input.toLowerCase()) &&
          !event.session.roomSystems.some(
            (existingRoomSystem) => existingRoomSystem.id === roomSystem.id,
          ),
      ) || [];

    return filteredRoomSystems.map((roomSystem) => ({
      roomSystemId: roomSystem.id,
      label: roomSystem.name,
      id: roomSystem.id,
      name: roomSystem.name,
      organisationId: roomSystem.organisation.id,
      organisationName: roomSystem.organisation.name,
      conflictingSessions: roomSystem.sessions?.conflictingSessions || [],
    }));
  };

  const handleSubmit = async () => {
    if (!organisation?.id) {
      return;
    }

    await addRoomSystemsToEvent({
      orgId: organisation.id,
      eventId: event.id,
      payload: {
        participants: newRoomSystems.map((roomSystem) => ({
          roomSystemId: roomSystem.id,
        })),
      },
    });
  };

  const handleOpenChange = (open: boolean) => {
    if (!open && isMenuOpen) {
      return;
    }
    if (!open) {
      reset();
      setNewRoomSystems([]);
      setValue("");
    }
    setIsOpen(open);
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant="outline" className="w-full">
          Register Room System
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Register Room Systems</DialogTitle>
          <DialogDescription>
            Add one or more room systems to this event
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(handleSubmit)}
              className="flex flex-col gap-4"
            >
              <div className="flex flex-col gap-2">
                <Label>Room System Name</Label>
                <AsyncSelect<RoomSystemOption>
                  unstyled
                  menuPortalTarget={document.body}
                  styles={{
                    menuPortal: (base) => ({
                      ...base,
                      pointerEvents: "auto",
                      zIndex: 100,
                    }),
                  }}
                  components={{
                    DropdownIndicator: SearchIndicator,
                    ClearIndicator: () => null,
                    Option: RoomSystemSelectOption,
                    IndicatorSeparator: () => null,
                  }}
                  classNames={
                    reactSelectClassNames as ClassNamesConfig<
                      RoomSystemOption,
                      false,
                      GroupBase<RoomSystemOption>
                    >
                  }
                  ref={form.register("roomSystems").ref}
                  placeholder="Type room system name"
                  noOptionsMessage={() => "Type room system name"}
                  loadOptions={loadOptions}
                  isDisabled={form.formState.isSubmitting}
                  inputValue={value}
                  onInputChange={(value: string) => {
                    setValue(value);
                  }}
                  value={null}
                  onMenuOpen={() => {
                    setIsMenuOpen(true);
                  }}
                  onMenuClose={() => {
                    setIsMenuOpen(false);
                  }}
                  onChange={(value) => {
                    if (value) {
                      addRoomSystem(value);
                      setValue("");
                      return null;
                    }
                  }}
                />
              </div>
              <RegisterRoomSystemToEventModalTable
                roomSystems={newRoomSystems}
                setRoomSystems={setNewRoomSystems}
              />
            </form>
          </Form>
        </DialogBody>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">Cancel</Button>
          </DialogClose>
          <Button
            type="submit"
            disabled={isPending || newRoomSystems.length === 0}
            isAnimated={isPending}
            isLoading={isPending}
            onClick={handleSubmit}
          >
            Register Room System
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
